/**
 * @file src/world/performanceFigure.js
 * @description What performance mode draws where the planet was: the track's world-section DNA
 * figure, drawn live from the design library.
 *
 * The mode stops the 3D and leaves the centre black. This fills it with the figure the world was
 * released with — the one for the section of the world's narration this track is authored against,
 * so a track in `fauna` shows the fauna figure and one with no section shows the world itself.
 *
 * WHY DRAWN AND NOT THE IMAGE: the released `.webp` is a CARD — the figure sits in an upper band
 * with a printed border and a QR code under it, and the largest tier is 586x840, which is soft at
 * the size of a planet. There is nothing to crop here and it is crisp at any size.
 *
 * THE BUDGET IS THE POINT. This mode exists because rendering was breaking the audio on a 2016
 * MacBook Pro, and a room draws one of these per voice on the main thread beside the audio. So the
 * geometry is built ONCE — per world and section, cached for the life of the voice — and after that
 * a redraw is only the fit and the stroke.
 *
 * The build is NOT uniformly cheap, and the spread is the number that matters. These are L-systems
 * and the iteration count is authored, so cost is a step function of what the world says, not an
 * average. Measured against the real library on an M-series Mac:
 *
 *   geography 255 segs 0.07ms · history 242 0.08ms · fauna 1 0.07ms · world 5940 4.39ms
 *   flora, abundant           24,384 segs   6.10ms
 *   society, renewable energy 59,049 segs  14.23ms   ← the worst a release can ask for
 *
 * Society's renewable and organic rules are quadratic Koch — nine symbols per symbol, five
 * iterations — so one authored word is the difference between 2,728 segments and 59,049. That build
 * runs SYNCHRONOUSLY, once, on the first toggle into the mode (never on load: nothing is built while
 * the mode is off), so a four-voice room of society/renewable tracks can spend ~60ms here in one
 * burst, and several times that on the machine this mode exists for. It is one-time and it is at a
 * gesture, which is why it is left on the critical path — but it is a hitch, not free, and if it
 * shows on the 2016 MBP the fix is to build on an idle callback, not to draw less.
 *
 * Nothing here runs on a frame clock: it draws when the figure changes and when the box it lives in
 * changes, and otherwise it sits still.
 *
 * It owns its own canvas and nothing else. The container is LENT — a realm voice's cell, or the
 * single orbiter's canvas frame — and is left exactly as it was found on dispose, for the same
 * reason `noRenderHost` will not touch cell DOM: the interface in that box is what the mode leaves
 * the person with, and it must survive every toggle.
 *
 * TWO THINGS, NOT ONE. The dark belongs to the MODE and goes up whenever it is on; the figure is
 * drawn on top when there is one to draw. That separation is the whole answer to "a world whose
 * figure cannot be built shows black" — black is a thing this paints, not a thing it leaves behind.
 * The scene it replaces painted a skybox across this box, and the interface layered over it was
 * drawn to sit on a dark scene: inside this app the frame underneath is black anyway and its absence
 * never showed, but in an embedding host a light theme reached straight through and took the
 * interface with it.
 */
import {
  buildWorldFigure,
  projectWorldFigure,
  buildGeographyFigure,
  buildFloraFigure,
  buildFaunaFigure,
  buildSocietyFigure,
  buildHistoryFigure,
  dnaInputsFromRelease,
  figureSectionForNarration,
  drawSegments,
} from 'plantasia.space-design/dna';

/**
 * The stroke width the released figures were drawn at. Not a taste setting: the authoring form
 * passed 1.5 to all six, so anything else stops matching the image the release shipped.
 */
const RELEASED_LINE_WIDTH = 1.5;

/**
 * How much of the frame the figure takes, as a fraction of its shorter side.
 *
 * It stands in for the planet, so it is sized like one. Measured against the 3D at the same camera,
 * a world fills a little over two fifths of the frame; drawn edge to edge instead, the figure stops
 * reading as an object in the middle of a space and starts reading as a backdrop, and it collides
 * with the interface that is layered over it.
 */
const FRAME_FRACTION = 0.42;

/**
 * Build the section's figure into the flat `{ segs, bounds }` a draw takes.
 *
 * The world figure is the only one with a camera, so it arrives in two parts — the expensive
 * camera-independent build and a projection. Both run here, once: a static figure has one camera,
 * the one its own inputs imply. The built figure is kept alongside, because moving that camera
 * without rebuilding is exactly what the movement work needs.
 *
 * The five section figures are given NO gravity and NO seed, and that is deliberate. The form that
 * authored the released images passed planet metrics to the world figure alone, so every released
 * section figure was drawn at the default gravity. `dnaInputsFromRelease` already reproduces that;
 * this must not add to what it hands over.
 */
function buildFigure(section, inputs) {
  if (section === 'world') {
    const built = buildWorldFigure(inputs.world);
    return { ...projectWorldFigure(built), built };
  }
  const build = {
    geography: buildGeographyFigure,
    flora: buildFloraFigure,
    fauna: buildFaunaFigure,
    society: buildSocietyFigure,
    history: buildHistoryFigure,
  }[section];
  if (!build) return null;
  return { ...build(inputs[section]), built: null };
}

/**
 * @param {{ container: HTMLElement | null }} opts the element to draw inside. A realm voice's cell,
 *   or the frame the single orbiter's 3D canvas lives in. Borrowed, never modified.
 */
export function createPerformanceFigure({ container } = {}) {
  const usable =
    container &&
    typeof container.appendChild === 'function' &&
    typeof document !== 'undefined';

  /** The track + world this voice is playing, or null before its session resolves. */
  let source = null;
  /** Whether the mode is on for this voice. Both this and a source are needed to draw. */
  let enabled = false;
  /** The mounted layer — the mode's dark, plus the canvas the figure is drawn on. */
  let layer = null;
  /** Whether a figure is actually on the canvas, as opposed to the dark merely being up. */
  let painted = false;
  /** The built figure, and the identity it was built for — so the same world never rebuilds. */
  let figure = null;
  let figureKey = null;
  let color = null;
  let resizeObserver = null;
  /** A repaint already booked for the next frame, so a drag coalesces into one — see `observeResize`. */
  let resizeFrame = null;
  /** The shorter side the drawing on the canvas was fitted to, so a resize to the same size is free. */
  let paintedSide = 0;
  let disposed = false;
  /**
   * Whether WE gave the borrowed container its positioning context. The cell is lent and outlives
   * every voice in it, so an inline style left behind would change the containing block for whoever
   * mounts there next — the file's promise to leave it as found has to cover its style too, not just
   * its children.
   */
  let wroteContainerPosition = false;

  /**
   * The layer fills the container and centres a SQUARE canvas in it, because square is the shape
   * these figures were drawn in: the fit inside a released figure anchors at its own padding rather
   * than centring, so handing it a wide canvas would push the drawing into a corner. The square is
   * sized here rather than in CSS — `drawSegments` fits to the canvas's client box, so that box has
   * to be a number this code decided, not one a container query might resolve differently.
   */
  function mount() {
    if (layer || !usable) return null;
    const box = document.createElement('div');
    box.className = 'orb-performance-figure';
    box.setAttribute('aria-hidden', 'true');
    box.style.cssText = [
      'position:absolute',
      'inset:0',
      'z-index:1',
      'pointer-events:none',
      // The dark the 3D used to leave behind. A world's scene painted a skybox across this whole box
      // and the mode disposes it, so with nothing in its place the page shows through — inside this
      // app that is a black frame anyway and nothing looks wrong, but in an EMBEDDING host it is the
      // host's own background, and under a light theme the orbiter's interface is white-on-white.
      // The chrome in this box was drawn for a dark scene; this is the scene it was drawn against.
      'background:#000',
    ].join(';');

    const canvas = document.createElement('canvas');
    // Centred by its own position, not by the box's layout. The app's page-global sheet pins EVERY
    // canvas to `absolute; top:0; left:0` — the 3D canvas is the one it was written for — so a
    // canvas centred by flow gets dragged into the corner. This says where it goes outright.
    canvas.style.cssText = [
      'display:block',
      'position:absolute',
      'top:50%',
      'left:50%',
      'transform:translate(-50%,-50%)',
    ].join(';');
    box.appendChild(canvas);

    // The box is positioned against the container, so the container has to be a positioning
    // context. The realm's cells and the single orbiter's frame both already are; a stage that is
    // not gets told, the same way the per-tile load overlay does it.
    try {
      if (typeof window !== 'undefined' && window.getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
        wroteContainerPosition = true;
      }
    } catch {
      // A container we cannot measure is still one we can draw in.
    }
    container.appendChild(box);
    layer = { box, canvas };
    return layer;
  }

  /**
   * Give the container's positioning back, if WE were the ones who wrote it. Done on every unmount
   * and not only on dispose: the mode is toggled far more often than a voice is destroyed, and
   * between a toggle off and the next toggle on the cell is the interface's alone again.
   */
  function releaseContainerPosition() {
    if (!wroteContainerPosition) return;
    try {
      container.style.position = '';
    } catch {
      // A container that will not take a style back is one the voice no longer owns anyway.
    }
    wroteContainerPosition = false;
  }

  function unmount() {
    // The observer watches the layer's own box, so it has no meaning without one — and leaving it
    // connected is worse than useless: `observeResize` refuses to build a second one, so the NEXT
    // layer would never be observed and the figure would keep whatever size it last drew at.
    unobserveResize();
    layer?.box.remove();
    layer = null;
    painted = false;
    paintedSide = 0;
    releaseContainerPosition();
  }

  /** Resolve the section this track draws. No section means the world's own figure. */
  function resolveSection(track) {
    return figureSectionForNarration(track?.metadata?.worldNarrationLink?.section ?? null) ?? 'world';
  }

  /**
   * Build the geometry for the current source, or keep what is already built. The key is the world,
   * its version and the section — everything the drawing depends on and nothing else, so a session
   * update that changes neither redraws without rebuilding.
   */
  function ensureFigure() {
    const world = source?.entangledWorld ?? source?.track?.entangledWorld ?? null;
    if (!world) return null;
    const section = resolveSection(source?.track);
    const key = `${world.worldId ?? ''}@${world.version ?? ''}:${section}`;
    if (figure && figureKey === key) return figure;

    const inputs = dnaInputsFromRelease(world, { worldId: world.worldId ?? undefined });
    if (!inputs) return null;
    const next = buildFigure(section, inputs);
    if (!next || !next.segs?.length) return null;

    figure = next;
    figureKey = key;
    // The world's own colour, from the style matrix its release carries — the same one the released
    // card was printed in. Deriving it here rather than reading the app's theme is what makes the
    // drawing recognisably THIS world's rather than this orbiter's.
    color = inputs.elementColor.rgb;
    return figure;
  }

  /**
   * Size the canvas to a planet's worth of the frame, in the SHAPE of the figure it holds.
   *
   * The library's fit is a verbatim lift of the arithmetic the released images were drawn with: one
   * scale for both axes, anchored at its own 12px padding rather than centred. Given a square box a
   * tall figure therefore hugs the corner. Shaping the box to the figure's own proportions instead
   * leaves that arithmetic untouched — the drawing is identical to the pixel — and lets the centred
   * box do the centring. Squaring the figure by hand would have meant editing released art.
   */
  /** What the figure's shorter side works out to right now. Read on its own so a resize that
   * changes nothing can be recognised without touching the canvas. */
  function measureSide() {
    if (!layer) return 0;
    return Math.floor(
      Math.min(layer.box.clientWidth || 0, layer.box.clientHeight || 0) * FRAME_FRACTION,
    );
  }

  function sizeCanvas() {
    if (!layer) return 0;
    const side = measureSide();
    // A box with no measurable size — a stage still laying out, or a test environment that lays
    // nothing out — is left with the canvas's own default rather than a zero that would divide the
    // fit by nothing.
    if (side <= 0) return 0;

    const { minX, minY, maxX, maxY } = figure?.bounds ?? {};
    const width = maxX - minX;
    const height = maxY - minY;
    const aspect = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
      ? width / height
      : 1;

    const boxWidth = aspect >= 1 ? side : Math.max(1, Math.round(side * aspect));
    const boxHeight = aspect >= 1 ? Math.max(1, Math.round(side / aspect)) : side;
    layer.canvas.style.width = `${boxWidth}px`;
    layer.canvas.style.height = `${boxHeight}px`;
    return side;
  }

  /**
   * Fit and stroke. Cheap by construction — no geometry is touched.
   *
   * @returns {boolean} whether a figure is now on the canvas. The caller watches the box only when
   *   there is something worth re-fitting.
   */
  function paint() {
    if (!layer || !figure) return false;
    const side = sizeCanvas();
    try {
      drawSegments(layer.canvas, figure.segs, figure.bounds, {
        color,
        lineWidth: RELEASED_LINE_WIDTH,
      });
      painted = true;
      paintedSide = side;
      return true;
    } catch {
      // A canvas with no 2D context (a headless or memory-starved tab) falls back to bare dark —
      // never a half-drawn figure. The LAYER stays: it is the mode's black backdrop, not the
      // figure's, and taking it down here would hand the centre back to the page underneath. Inside
      // this app that page is black and nothing would look wrong; in an embedding host on a light
      // theme it is white, and the interface layered over it goes white-on-white — the exact failure
      // the backdrop was added to prevent, reached by the one path nobody would test.
      clearCanvas();
      // And nothing to re-fit, so nothing to watch for. A resize would only re-throw.
      unobserveResize();
      return false;
    }
  }

  /**
   * A redraw when the box changes SIZE, and only then.
   *
   * This is not a frame loop — but a drag is. `ResizeObserver` fires once per layout, so holding a
   * window edge delivers one callback per frame, per voice, and the redraw underneath it is a
   * `moveTo`+`lineTo` per segment: up to 59,049 of them for a society figure, ~118,000 canvas calls
   * a frame in a room. So two guards. The repaint is COALESCED onto the next frame, which collapses
   * a burst of layouts into one draw; and it is SKIPPED when the side works out the same, because
   * the observer also fires on the initial observe and on sub-pixel changes the fit rounds away.
   *
   * The figure's own aspect needs no guard here: it can only change when the figure does, and that
   * repaints through `apply` rather than through this.
   */
  function observeResize() {
    if (resizeObserver || !layer || typeof ResizeObserver === 'undefined') return;
    resizeObserver = new ResizeObserver(() => {
      if (disposed || !layer || resizeFrame !== null) return;
      resizeFrame = schedule(() => {
        resizeFrame = null;
        if (disposed || !layer) return;
        if (measureSide() === paintedSide) return;
        paint();
      });
    });
    resizeObserver.observe(layer.box);
  }

  /**
   * Book the coalescing frame. `requestAnimationFrame` where there is one — the repaint is a draw
   * and belongs with the frame that will show it — and a timeout where there is not, so a hidden
   * tab or a non-browser host still settles rather than holding a repaint forever.
   */
  function schedule(run) {
    if (typeof requestAnimationFrame === 'function') {
      return { id: requestAnimationFrame(run), cancel: cancelAnimationFrame };
    }
    return { id: setTimeout(run, 0), cancel: clearTimeout };
  }

  function unobserveResize() {
    resizeObserver?.disconnect();
    resizeObserver = null;
    if (resizeFrame) {
      resizeFrame.cancel(resizeFrame.id);
      resizeFrame = null;
    }
  }

  /** Wipe the drawing without taking the dark down with it. */
  function clearCanvas() {
    painted = false;
    paintedSide = 0;
    if (!layer) return;
    try {
      const ctx = layer.canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
    } catch {
      // No 2D context to clear means nothing was ever drawn on it.
    }
  }

  /** Draw, or take the drawing away. The one place that decides which. */
  function apply() {
    if (disposed) return;
    if (!enabled || !usable) {
      unobserveResize();
      unmount();
      return;
    }
    // Mounted for the MODE, not for the figure. What goes up first is the dark that stands in for
    // the released scene, and it goes up whether or not there turns out to be anything to draw on
    // it — "a world whose figure cannot be built shows black" means this black, not the host page's.
    mount();
    let built = null;
    try {
      built = ensureFigure();
    } catch (error) {
      // A malformed or half-written release must not take the voice down with it — the mode still
      // has to play. `built` is still null, so this falls through to bare dark, with a word about why.
      console.warn('[performanceFigure] could not build the world figure', error);
    }
    if (!built) {
      unobserveResize();
      clearCanvas();
      return;
    }
    // Watched only once there is genuinely something on the canvas to re-fit. A paint that failed
    // has already stopped watching, and re-observing here would hand it a resize to throw on.
    if (paint()) observeResize();
  }

  return {
    /**
     * The resolved session this voice is playing — `{ track, entangledWorld }`. Called again on
     * every session change; a change that leaves the world and the section alone rebuilds nothing.
     */
    setSource(next) {
      source = next ?? null;
      apply();
    },

    /** Whether the 3D is off for this voice. The mode's own seam owns this — see the scene profile. */
    setEnabled(value) {
      const next = Boolean(value);
      if (next === enabled) return;
      enabled = next;
      apply();
    },

    /** Whether a FIGURE is currently drawn. The mode's dark can be up without one. */
    get isDrawn() {
      return painted;
    },

    /** Whether the mode's dark backdrop is standing in for the released scene. */
    get isBackdropUp() {
      return Boolean(layer);
    },

    /** The section being drawn, or null when nothing is. */
    get section() {
      return painted && source ? resolveSection(source.track) : null;
    },

    dispose() {
      disposed = true;
      // Takes the layer down, stops the observer, cancels any booked frame, and gives the
      // container's positioning back — all four are the same "leave it as found" promise.
      unmount();
      figure = null;
      figureKey = null;
      source = null;
    },
  };
}
